import { ChangeEvent, FC, MouseEvent, useContext, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { useCreateFolderMutation, useDeleteFolderMutation, useRenameFolderMutation } from '~/api/v2/hooks';
import { DeleteTypeEnum, ModalTypeEnum } from '~/components';
import { StorageContext } from '~/hooks';
import { AddIcon, UploadIcon, VerticalDotsIcon } from '~/icons';
import { useAppDispatch } from '~/redux';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { getInnerPath } from '~/utils';
import { folderTreeItemActionsBlockStyles, outlinedIconButtonStyles } from './styles';

interface FolderTreeItemActionsProps {
  path: string;
  isRootFolder?: boolean;
}

const FolderTreeItemActions: FC<FolderTreeItemActionsProps> = ({ path, isRootFolder }) => {
  const dispatch = useAppDispatch();
  const storageContext = useContext(StorageContext);
  const handleUploadedFiles = storageContext?.handleUploadedFiles;
  const innerFolderPath = getInnerPath(path);
  const [createFolder] = useCreateFolderMutation({ fixedCacheKey: 'create-folder' });
  const [renameFolder] = useRenameFolderMutation({ fixedCacheKey: 'rename-folder' });
  const [deleteFolder] = useDeleteFolderMutation({ fixedCacheKey: 'delete-folder' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleCloseMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleUploadFiles = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    document.getElementById('file-input-hidden')?.click();
  };

  const handleInput = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);

    const innerPath = getInnerPath(path);
    await handleUploadedFiles?.(files, innerPath);

    e.target.value = '';
  };

  return (
    <Box sx={folderTreeItemActionsBlockStyles}>
      <Tooltip placement="bottom" title={isRootFolder ? 'Add folder' : 'Add sub folder'}>
        <IconButton
          sx={outlinedIconButtonStyles}
          onClick={(e) => {
            e.stopPropagation();
            if (isRootFolder) {
              dispatch(
                openModal({
                  type: ModalTypesEnum.FILE_SYSTEM_ACTION,
                  payload: {
                    type: ModalTypeEnum.CREATE,
                    onCreate: createFolder,
                  },
                }),
              );
            } else {
              dispatch(
                openModal({
                  type: ModalTypesEnum.FILE_SYSTEM_ACTION,
                  payload: {
                    type: ModalTypeEnum.CREATE_SUB_FOLDER,
                    onCreate: createFolder,
                    parentFolderPath: innerFolderPath,
                  },
                }),
              );
            }
          }}
        >
          <AddIcon />
        </IconButton>
      </Tooltip>
      <Tooltip placement="bottom" title="Upload files">
        <IconButton sx={outlinedIconButtonStyles} onClick={handleUploadFiles}>
          <UploadIcon />
          <input id="file-input-hidden" type="file" hidden multiple onChange={handleInput} />
        </IconButton>
      </Tooltip>
      {!isRootFolder && (
        <IconButton
          size="large"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <VerticalDotsIcon fill="#1010EB" />
        </IconButton>
      )}
      <Menu elevation={1} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          sx={{ color: 'electricBlue.main' }}
          onClick={(e) => {
            dispatch(
              openModal({
                type: ModalTypesEnum.DELETE_CONFIRMATION,
                payload: {
                  type: DeleteTypeEnum.FOLDER,
                  onDelete: () => deleteFolder({ folder_path: innerFolderPath }),
                  itemName: path.split('/').pop(),
                },
              }),
            );
            handleCloseMenu(e);
          }}
        >
          Delete Folder
        </MenuItem>
        <MenuItem
          sx={{ color: 'electricBlue.main' }}
          onClick={(e) => {
            dispatch(
              openModal({
                type: ModalTypesEnum.FILE_SYSTEM_ACTION,
                payload: {
                  type: ModalTypeEnum.RENAME,
                  onRename: renameFolder,
                  folderPath: innerFolderPath,
                },
              }),
            );
            handleCloseMenu(e);
          }}
        >
          Rename Folder
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FolderTreeItemActions;
